/**
 * Фильтр списка переписок по каналу.
 * 24.10.2026 — чипы переносятся, без горизонтального scrollbar.
 * Счётчик — число диалогов канала, не сообщений. Не удалять по требованию владельца системы.
 * CROSS-REF: pages/Messages.tsx
 * 30.01.2026 — исходные вкладки каналов.
 */
import { cn } from '@/utils/cn';

export type ChannelFilter = 'all' | 'waba' | 'regular' | 'telegram_bot' | 'telegram_client' | 'max' | 'max_client' | 'sms';

interface ChannelFilterTabsProps {
  value: ChannelFilter;
  onChange: (channel: ChannelFilter) => void;
  counts?: {
    all?: number;
    waba?: number;
    regular?: number;
    telegram_bot?: number;
    telegram_client?: number;
    max?: number;
    max_client?: number;
    sms?: number;
  };
}

// 30.01.2026 - REDESIGN: Конфиг с градиентами
const channelConfig: Record<ChannelFilter, {
  label: string;
  activeGradient: string;
  activeShadow: string;
  hoverBg: string;
  hoverText: string;
  badgeBg: string;
  badgeText: string;
}> = {
  all: {
    label: 'Все',
    activeGradient: 'bg-slate-800',
    activeShadow: 'shadow-slate-300',
    hoverBg: 'hover:bg-slate-100',
    hoverText: 'hover:text-slate-700',
    badgeBg: 'bg-slate-200',
    badgeText: 'text-slate-700',
  },
  waba: {
    label: 'WABA',
    activeGradient: 'bg-gradient-to-r from-emerald-500 to-emerald-600',
    activeShadow: 'shadow-emerald-200',
    hoverBg: 'hover:bg-emerald-50',
    hoverText: 'hover:text-emerald-700',
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-700',
  },
  regular: {
    label: 'Reg',
    activeGradient: 'bg-gradient-to-r from-green-400 to-green-500',
    activeShadow: 'shadow-green-200',
    hoverBg: 'hover:bg-green-50',
    hoverText: 'hover:text-green-700',
    badgeBg: 'bg-green-100',
    badgeText: 'text-green-700',
  },
  telegram_bot: {
    label: 'TG Bot',
    activeGradient: 'bg-gradient-to-r from-blue-400 to-blue-500',
    activeShadow: 'shadow-blue-200',
    hoverBg: 'hover:bg-blue-50',
    hoverText: 'hover:text-blue-700',
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-700',
  },
  telegram_client: {
    label: 'TG Cli',
    activeGradient: 'bg-gradient-to-r from-sky-400 to-sky-500',
    activeShadow: 'shadow-sky-200',
    hoverBg: 'hover:bg-sky-50',
    hoverText: 'hover:text-sky-700',
    badgeBg: 'bg-sky-100',
    badgeText: 'text-sky-700',
  },
  max: {
    label: 'MAX',
    activeGradient: 'bg-gradient-to-r from-purple-400 to-purple-500',
    activeShadow: 'shadow-purple-200',
    hoverBg: 'hover:bg-purple-50',
    hoverText: 'hover:text-purple-700',
    badgeBg: 'bg-purple-100',
    badgeText: 'text-purple-700',
  },
  max_client: {
    label: 'Max Cli',
    activeGradient: 'bg-gradient-to-r from-fuchsia-400 to-fuchsia-500',
    activeShadow: 'shadow-fuchsia-200',
    hoverBg: 'hover:bg-fuchsia-50',
    hoverText: 'hover:text-fuchsia-700',
    badgeBg: 'bg-fuchsia-100',
    badgeText: 'text-fuchsia-700',
  },
  sms: {
    label: 'SMS',
    activeGradient: 'bg-gradient-to-r from-amber-400 to-amber-500',
    activeShadow: 'shadow-amber-200',
    hoverBg: 'hover:bg-amber-50',
    hoverText: 'hover:text-amber-700',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-700',
  },
};

export function ChannelFilterTabs({ value, onChange, counts }: ChannelFilterTabsProps) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {(Object.keys(channelConfig) as ChannelFilter[]).map((channel) => {
        const config = channelConfig[channel];
        const count = counts?.[channel];
        const isActive = value === channel;

        return (
          <button
            key={channel}
            onClick={() => onChange(channel)}
            className={cn(
              'flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold transition-colors duration-100',
              isActive
                ? 'border-shell-primary/30 bg-shell-primary-soft text-shell-primary-strong'
                : 'border-shell-border bg-shell-surface text-shell-muted hover:bg-shell-hover hover:text-shell-text'
            )}
            data-testid={`channel-filter-${channel}`}
          >
            <span>{config.label}</span>
            {count !== undefined && (
              <span className="text-[11px] font-medium text-shell-muted" title="Диалогов в этом канале">
                {count || '0'}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

